import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import {
  WEBPROFIT_CALENDLY_URL,
  WEBPROFIT_WHATSAPP_URL,
  buildCalendlyEmbedUrl,
} from "../utils/publicContact";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DF4728",
  dark: "#19242F",
  soft: "#F7F8FA",
  green: "#2A9D67",
};

const COPY = {
  sales: {
    eyebrow: "Hablar con WebProfit",
    title: "¿Querés escribirnos o preferís verlo en una llamada?",
    description:
      "Podés hacer una consulta rápida por WhatsApp o reservar una videollamada de 15 minutos. Si querés conocer el producto, hacer tus preguntas y ver cómo WebProfit puede encajar en tu negocio, te conviene la llamada.",
  },
  support: {
    eyebrow: "Contacto y soporte",
    title: "¿Cómo querés que te ayudemos?",
    description:
      "Para una duda puntual podés escribirnos por WhatsApp. Si necesitás que revisemos tu caso, entender una herramienta o ver cómo aplicar WebProfit a tu negocio, podés reservar una llamada con un implementador.",
  },
};

// WP-STANDARD | PUBLIC CONTACT CHOOSER
// STATUS: STATIC_PASS · RESPONSIVE_PASS_BY_REVIEW · RUNTIME_PENDING
// Reusable boundary used by every public sales/support CTA. It intentionally shows
// the channel choice before leaving WebProfit and keeps Calendly embedded in-site.
export default function PublicContactDialog({
  open,
  onClose,
  mode = "sales",
  sourceLabel = "",
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [view, setView] = React.useState("choice");
  const copy = COPY[mode] || COPY.sales;
  const calendlyEmbedUrl = React.useMemo(() => buildCalendlyEmbedUrl(), []);

  React.useEffect(() => {
    if (!open) setView("choice");
  }, [open]);

  const openWhatsapp = () => {
    // Keep WhatsApp secondary but immediately usable after the visitor understands
    // that a product-fit conversation is usually better handled by a short call.
    window.open(WEBPROFIT_WHATSAPP_URL, "_blank", "noopener,noreferrer");
    onClose?.();
  };

  const openCalendlyExternal = () => {
    window.open(WEBPROFIT_CALENDLY_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={view === "calendar" ? "md" : "sm"}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 5,
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0 28px 90px rgba(9,14,19,.28)",
        },
      }}
    >
      <DialogContent sx={{ p: 0, overflow: view === "calendar" ? "hidden" : "auto" }}>
        {view === "choice" ? (
          <Box>
            <Box
              sx={{
                position: "relative",
                px: { xs: 2.2, sm: 4 },
                pt: { xs: 4.5, sm: 5 },
                pb: { xs: 2.8, sm: 3.5 },
                bgcolor: "#0E151D",
                color: "#fff",
              }}
            >
              <IconButton
                aria-label="Cerrar"
                onClick={onClose}
                sx={{ position: "absolute", top: 12, right: 12, color: "rgba(255,255,255,.7)" }}
              >
                <CloseRoundedIcon />
              </IconButton>

              <Stack direction="row" spacing={1} alignItems="center">
                <SupportAgentRoundedIcon sx={{ color: "#FF9D84" }} />
                <Typography sx={{ color: "#FFB19A", fontWeight: 900, letterSpacing: ".09em", textTransform: "uppercase", fontSize: ".72rem" }}>
                  {copy.eyebrow}
                </Typography>
              </Stack>
              <Typography sx={{ mt: 1.4, maxWidth: 560, fontWeight: 950, letterSpacing: "-.045em", lineHeight: 1.03, fontSize: { xs: "1.9rem", sm: "2.5rem" } }}>
                {copy.title}
              </Typography>
              <Typography sx={{ mt: 1.4, maxWidth: 600, color: "rgba(255,255,255,.67)", lineHeight: 1.6, fontSize: { xs: ".92rem", sm: "1rem" } }}>
                {copy.description}
              </Typography>
              {sourceLabel ? (
                <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,.42)", fontSize: ".74rem" }}>
                  Consulta: {sourceLabel}
                </Typography>
              ) : null}
            </Box>

            <Stack spacing={1.4} sx={{ p: { xs: 2, sm: 3 } }}>
              <Paper
                elevation={0}
                sx={{ p: { xs: 2, sm: 2.4 }, borderRadius: 3.5, border: "1px solid rgba(243,90,58,.2)", bgcolor: "#FFF8F5" }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.2}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={.9}>
                      <CalendarMonthRoundedIcon sx={{ color: BRAND.orange }} />
                      <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1.04rem" }}>Agendar una llamada</Typography>
                      <Chip size="small" label="Recomendado" sx={{ bgcolor: "rgba(243,90,58,.11)", color: "#B9361E", fontWeight: 900 }} />
                    </Stack>
                    <Typography sx={{ mt: .9, color: "rgba(25,36,47,.64)", lineHeight: 1.5, fontSize: ".86rem" }}>
                      15 minutos con un implementador de WebProfit para conocer tu negocio, responder preguntas y mostrarte qué herramientas pueden servirte.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CalendarMonthRoundedIcon />}
                  onClick={() => setView("calendar")}
                  sx={{ mt: 1.8, minHeight: 48, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: BRAND.orangeDark } }}
                >
                  Elegir día y horario
                </Button>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: { xs: 2, sm: 2.4 }, borderRadius: 3.5, border: "1px solid rgba(25,36,47,.1)", bgcolor: BRAND.soft }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <WhatsAppIcon sx={{ color: BRAND.green }} />
                  <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1rem" }}>Mandar un mensaje por WhatsApp</Typography>
                </Stack>
                <Typography sx={{ mt: .8, color: "rgba(25,36,47,.6)", lineHeight: 1.5, fontSize: ".84rem" }}>
                  Ideal para una consulta rápida. Si querés conocer el producto o analizar cómo encaja en tu negocio, probablemente coordinemos una llamada desde ahí.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  onClick={openWhatsapp}
                  sx={{ mt: 1.5, minHeight: 46, borderRadius: 999, color: BRAND.dark, borderColor: "rgba(25,36,47,.2)", textTransform: "none", fontWeight: 900, "&:hover": { borderColor: BRAND.dark, bgcolor: "rgba(25,36,47,.03)" } }}
                >
                  Continuar por WhatsApp
                </Button>
              </Paper>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ height: isMobile ? "100vh" : "min(790px, 88vh)", display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: { xs: 1, sm: 2 }, py: 1, borderBottom: "1px solid rgba(25,36,47,.08)" }}>
              <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => setView("choice")} sx={{ color: BRAND.dark, textTransform: "none", fontWeight: 900 }}>
                Volver
              </Button>
              <Stack direction="row" spacing={.7}>
                <Button endIcon={<LaunchRoundedIcon />} onClick={openCalendlyExternal} sx={{ display: { xs: "none", sm: "inline-flex" }, color: "rgba(25,36,47,.68)", textTransform: "none", fontWeight: 800 }}>
                  Abrir aparte
                </Button>
                <IconButton aria-label="Cerrar" onClick={onClose} sx={{ color: BRAND.dark }}><CloseRoundedIcon /></IconButton>
              </Stack>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, bgcolor: "#fff" }}>
              <Box
                component="iframe"
                title="Agendar llamada con WebProfit"
                src={calendlyEmbedUrl}
                allow="camera; microphone; fullscreen"
                sx={{ width: "100%", height: "100%", minHeight: 0, border: 0, display: "block" }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
